import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLingui } from '@lingui/react/macro';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import AnimatedSpinner from '@rover/components/src/typography/AnimatedSpinner';
import { Box, Button, Flex, Heading } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import Review from '@rover/react-lib/src/components/modals/Review';
import ReviewReply from '@rover/react-lib/src/components/modals/Review/ReviewReply';
import { isReplyable } from '@rover/react-lib/src/components/modals/Review/utils';
import {
  apiPersonDetailReviewsList,
  getApiPersonDetailReviewsListQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-person-detail-reviews-list';
import {
  apiReviewsRatingDetailRetrieve,
  getApiReviewsRatingDetailRetrieveQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-reviews-rating-detail-retrieve';
import { ApiPersonDetailReviewsListPetType as PetTypeReview } from '@rover/rsdk/src/apiClient/latest/model/apiPersonDetailReviewsListPetType';
import type { PaginatedReviewDispatchList } from '@rover/rsdk/src/apiClient/latest/model/paginatedReviewDispatchList';
import type { ReviewDispatch } from '@rover/rsdk/src/apiClient/latest/model/reviewDispatch';
import { useCurrentUser } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';
import { Logger } from '@rover/rsdk/src/modules/Logging';
import useBrandSpecificPets from '@rover/shared/js/hooks/useBrandSpecificPets';
import { isCatOnlyAvailablePetTypesBasedOnPetTypes } from '@rover/utilities/getAvailablePetTypes/availablePetTypesUtils';

import PetTypeReviewFilter from './PetTypeReviewFilter';

type Props = {
  opk: string;
  highlightedReviewOpk?: string | null;
  scrollIndex: number;
  sitterAvatar: string;
  onReplyClick: (review: ReviewDispatch) => void;
  onDelete: (review: ReviewDispatch) => void;
  onLearnMore: () => void;
  petTypeFilter?: (typeof PetTypeReview)[keyof typeof PetTypeReview] | null;
  onFilterChange?: (filter: (typeof PetTypeReview)[keyof typeof PetTypeReview] | null) => void;
  title?: string;
  seeMoreButtonClick?: boolean;
  children?: JSX.Element | null;
  showRating?: boolean;
};

const getOpkFromReviewUrl = (url: string | undefined): string | null => {
  if (!url) return null;
  const match = url.match(/\/reviews\/[^/]+\/([^/]+)\/?/);
  return match ? match[1] : null;
};

const FullReviewsList = ({
  opk,
  highlightedReviewOpk = null,
  scrollIndex,
  sitterAvatar,
  onReplyClick,
  onDelete,
  onLearnMore,
  petTypeFilter,
  onFilterChange,
  title,
  seeMoreButtonClick = false,
  children = null,
  showRating = false,
}: Props): JSX.Element => {
  const { t } = useLingui();
  const theme = useTheme();
  const availablePets = useBrandSpecificPets();
  const isCatOnlyBrand = isCatOnlyAvailablePetTypesBasedOnPetTypes(availablePets);
  const handleFilterChange = (
    filter: (typeof PetTypeReview)[keyof typeof PetTypeReview] | null
  ): void => {
    if (onFilterChange) {
      onFilterChange(filter);
    }
  };

  const reviewTitle = title ? (
    <Heading size="400" fontWeight="semibold">
      {title}
    </Heading>
  ) : null;

  const reviewListRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | undefined>(undefined);
  const wasInitiallyScrolled = useRef(false);

  const fetchReviews = async ({ pageParam = 1 }): Promise<PaginatedReviewDispatchList> =>
    apiPersonDetailReviewsList(opk, {
      page: pageParam,
      pageSize: 10,
      ...(isCatOnlyBrand &&
        petTypeFilter !== undefined &&
        petTypeFilter !== null && { petType: petTypeFilter }),
    });

  const {
    data: reviewsList,
    fetchNextPage,
    hasNextPage,
    isFetching: isReviewListFetching,
  } = useInfiniteQuery(
    [getApiPersonDetailReviewsListQueryKey(opk), isCatOnlyBrand, petTypeFilter],
    fetchReviews,
    {
      getNextPageParam: (lastPage, allPages) => {
        const totalCount = lastPage.count ?? 0;
        const loadedCount = allPages.flatMap((p) => p.results).length;
        return loadedCount < totalCount ? allPages.length + 1 : undefined;
      },
      enabled: !!opk,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    }
  );

  const rawReviews = (reviewsList?.pages ?? []).flatMap((page) => page.results ?? []);

  const isFirstPageLoaded = (reviewsList?.pages?.length ?? 0) > 0;
  const matchingReviewInList = highlightedReviewOpk
    ? rawReviews.find((review) => getOpkFromReviewUrl(review.url) === highlightedReviewOpk)
    : undefined;

  // If first page is already loaded but the highlighted review is not present, we enable
  // the React-Query hook for fetching it individually
  const shouldFetchHighlightedReview =
    !!highlightedReviewOpk && isFirstPageLoaded && !matchingReviewInList;

  const { data: fetchedHighlightedReview, isFetching: isHighlightedReviewFetching } = useQuery(
    getApiReviewsRatingDetailRetrieveQueryKey(highlightedReviewOpk ?? ''),
    () => apiReviewsRatingDetailRetrieve(highlightedReviewOpk ?? ''),
    {
      enabled: shouldFetchHighlightedReview,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    }
  );

  const isFetching = isReviewListFetching || isHighlightedReviewFetching;

  const flattenedReviews = useMemo(() => {
    // Don't show any review if the highlighted review is loading to avoid glitches
    if (isHighlightedReviewFetching) {
      return [];
    }
    // No extra processing is needed if there's not highlightReviewOpk
    if (!highlightedReviewOpk) {
      return rawReviews;
    }
    // If the highlighted review is initially in the list, we move it to the top
    // to avoid scrolling (this is coming from the "See all" button in the
    // search card review snippet, we don't want to hide other reviews by scrolling)
    if (matchingReviewInList) {
      return [
        matchingReviewInList,
        ...rawReviews.filter((review) => review !== matchingReviewInList),
      ];
    }
    // The review fetched individually is also moved to the top
    if (fetchedHighlightedReview) {
      return [fetchedHighlightedReview, ...rawReviews];
    }
    return rawReviews;
  }, [
    isHighlightedReviewFetching,
    rawReviews,
    highlightedReviewOpk,
    matchingReviewInList,
    fetchedHighlightedReview,
  ]);

  const highlightedIndex = highlightedReviewOpk && flattenedReviews.length > 0 ? 0 : -1;

  const { user } = useCurrentUser();

  const effectiveScrollIndex = highlightedIndex >= 0 ? highlightedIndex : scrollIndex;

  // Scroll to the review that was clicked or to the highlighted review
  useEffect(() => {
    if (wasInitiallyScrolled.current || effectiveScrollIndex < 0 || flattenedReviews.length <= 0)
      return;
    setTimeout(() => {
      if (process.env.JS_ENV_CLIENT) {
        const reviewToFocus = document.getElementById(`review-${effectiveScrollIndex}`);
        if (reviewToFocus === null) return;
        wasInitiallyScrolled.current = true;
        reviewToFocus.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
        const firstChild = reviewToFocus.firstChild as HTMLElement;
        if (!seeMoreButtonClick) {
          firstChild.style.backgroundColor = theme.background.accent;
          firstChild.style.transition = 'background-color 0.2s ease-in-out';
          setTimeout(() => {
            firstChild.style.backgroundColor = '';
          }, 2000);
        }
        const focusTarget = reviewToFocus.querySelector<HTMLElement>('[role="group"]');
        (focusTarget ?? reviewToFocus).focus();
      }
    }, 100);
  }, [effectiveScrollIndex, flattenedReviews.length, theme, seeMoreButtonClick, isFetching]);

  // For infinite scroll
  const scrollRef = useCallback(
    (node) => {
      if (isFetching) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          if (hasNextPage) fetchNextPage();
        }
        Logger.info('sitter_profile_load_more_reviews');
      });
      if (node) observerRef.current.observe(node);
    },
    [hasNextPage, isFetching, fetchNextPage]
  );

  const ownProfile = user?.opk === opk;

  return (
    <>
      <Flex
        width="100%"
        flexDirection={['column', 'row']}
        alignItems="center"
        justifyContent="space-between"
        mb="6x"
      >
        {reviewTitle}{' '}
        <PetTypeReviewFilter
          onChange={handleFilterChange}
          initialPetType={isCatOnlyBrand ? PetTypeReview.cat : undefined}
        />
      </Flex>
      <Box ref={reviewListRef}>
        {children}
        {flattenedReviews.map((review, index) => {
          const hasReplyElements =
            isReplyable(review) && (review?.response || (!review?.response && ownProfile));
          return (
            <Box
              width="100%"
              key={review.url}
              id={`review-${index}`}
              sx={{ paddingBottom: !hasReplyElements ? '24px' : '0px' }}
            >
              <Box p="4x" borderRadius="8px">
                <Review
                  review={review}
                  {...(index === flattenedReviews.length - 1 && hasNextPage && { ref: scrollRef })}
                  onLearnMore={onLearnMore}
                  full
                  showRating={showRating}
                />
              </Box>
              {isReplyable(review) && review && 'response' in review && review.response && (
                <Box pt="2x" sx={{ paddingBottom: '40px' }}>
                  <ReviewReply
                    reply={review.response}
                    avatarUrl={sitterAvatar}
                    {...(ownProfile && { onDelete: () => onDelete(review) })}
                  />
                </Box>
              )}
              {isReplyable(review) && review && !review.response && ownProfile && (
                <Box px="1x" pt="6x" sx={{ paddingBottom: '40px' }}>
                  <Button fullWidth onClick={() => onReplyClick(review)} size="small">
                    {t`Reply`}
                  </Button>
                </Box>
              )}
            </Box>
          );
        })}
        {isFetching && (
          <Box mb="8x" height="8x">
            <AnimatedSpinner />
          </Box>
        )}
      </Box>
    </>
  );
};

export default FullReviewsList;
